import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Drawer } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { AuthStoreContext } from '../Root';
import Header from '../Header';
import { SideBarState } from '../SideBar';
import DashboardSideBar from './DashboardSideBar';
import { Selection } from './selection';
import { readSelectionCookie, writeSelectionCookie } from './selection-cookie';
import { findDefaultSelection } from './default-selection';

const DRAWER_WIDTH = 250;

export interface DashboardOutletContext {
    selection: Selection;
    onSelect: (sel: Selection) => void;
    selectSilent: (sel: Selection) => void;
}

//=================================================================================================
export default function DashboardShellLayout() {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user!;
    const navigate = useNavigate();
    const location = useLocation();

    const showBackButton = matchPath('/device/:id', location.pathname) !== null;

    const [sideBar, setSideBar] = useState(SideBarState.open);
    const [selection, setSelection] = useState<Selection>(
        () => readSelectionCookie() ?? { kind: 'node', nodeId: user.node_id },
    );

    // Храним последний pathname в ref, чтобы handleSelect не зависел от location.pathname
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;

    useEffect(() => {
        if (!readSelectionCookie()) {
            findDefaultSelection(user.node_id).then((sel) => setSelection(sel));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sideBarBehavior = useCallback((state: SideBarState) => {
        setSideBar(state);
    }, []);

    const handleSelect = useCallback(
        (sel: Selection) => {
            setSelection(sel);
            writeSelectionCookie(sel);
            if (pathnameRef.current !== '/') {
                navigate('/');
            }
        },
        [navigate],
    );

    // Обновляет selection без навигации. Нужен, например, чтобы подсвестить
    // устройство в сайдбаре при открытии его статистики по `/device/:id`.
    const handleSelectSilent = useCallback((sel: Selection) => {
        setSelection(sel);
        writeSelectionCookie(sel);
    }, []);

    const outletContext = useMemo(
        () =>
            ({ selection, onSelect: handleSelect, selectSilent: handleSelectSilent }) satisfies DashboardOutletContext,
        [selection, handleSelect, handleSelectSilent],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header sideBar={{ state: sideBar, setState: sideBarBehavior }} />
            <Box sx={{ backgroundColor: showBackButton ? 'background.paper' : 'background.default' }}>
                <Button
                    variant="text"
                    size="small"
                    startIcon={<ArrowBackIcon sx={{ width: 14, height: 14 }} />}
                    onClick={() => navigate('/')}
                    sx={{ textTransform: 'none', visibility: showBackButton ? 'visible' : 'hidden' }}
                >
                    Назад к измерениям
                </Button>
            </Box>
            <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
                <Box
                    sx={(theme) => ({
                        flexShrink: 0,
                        overflow: 'hidden',
                        bgcolor: 'sidebar.main',
                        width: sideBar === SideBarState.open ? DRAWER_WIDTH : 0,
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    })}
                >
                    <Drawer
                        variant="permanent"
                        sx={{
                            height: '100%',
                            '& .MuiDrawer-paper': {
                                position: 'relative',
                                width: DRAWER_WIDTH,
                                height: '100%',
                                overflowY: 'hidden',
                                backgroundColor: 'sidebar.main',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                            },
                        }}
                    >
                        <DashboardSideBar selection={selection} onSelect={handleSelect} />
                    </Drawer>
                </Box>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        pl: 2,
                        pr: 2,
                        minWidth: 0,
                        minHeight: 0,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Outlet context={outletContext} />
                </Box>
            </Box>
        </Box>
    );
}
